import { useContext, useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  GeoJSON,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faLink,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CasesContext from "contexts/CasesContext";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import egibService from "api/egibService";
import { IEgibParcel } from "dto/Egib/IEgibParcel";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

// WMS GUGiK — Krajowa Integracja Ewidencji Gruntów (warstwa działek).
const KIEG_URL =
  "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow";

// Geometria działki (GeoJSON z BE) → granice do dopasowania widoku.
const FitToParcel = ({ geom }: { geom: object | null }) => {
  const map = useMap();
  useEffect(() => {
    if (!geom) return;
    try {
      const bounds = L.geoJSON(geom as any).getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { maxZoom: 19, padding: [20, 20] });
    } catch {
      /* ignore */
    }
  }, [geom, map]);
  return null;
};

// Klik na mapie → wyszukanie działki po współrzędnych.
const ClickHandler = ({
  onPick,
}: {
  onPick: (lng: number, lat: number) => void;
}) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lng, e.latlng.lat),
  });
  return null;
};

const EgibModule = () => {
  const { cases } = useContext(CasesContext);

  const [idInput, setIdInput] = useState("");
  const [parcel, setParcel] = useState<IEgibParcel | null>(null);
  const [loading, setLoading] = useState(false);
  const [showParcelsLayer, setShowParcelsLayer] = useState(true);

  const [selectedCase, setSelectedCase] = useState<ICaseSimple | null>(null);

  const { data: caseParcels = [], refetch: refetchCaseParcels } =
    egibService.useGetCaseParcels(selectedCase?.Id);
  const linkMutation = egibService.useLinkParcel();
  const unlinkMutation = egibService.useUnlinkParcel();

  const geom = useMemo<object | null>(() => {
    if (!parcel?.GeometryGeoJson) return null;
    try {
      return JSON.parse(parcel.GeometryGeoJson);
    } catch {
      return null;
    }
  }, [parcel]);

  const handleError = (text: string) =>
    notification({ type: ENotificationType.ERROR, title: "EGiB", text });

  const findById = async () => {
    const id = idInput.trim();
    if (!id) return;
    setLoading(true);
    try {
      const p = await egibService.getParcelById(id);
      setParcel(p);
    } catch {
      handleError("Nie znaleziono działki o podanym identyfikatorze.");
    } finally {
      setLoading(false);
    }
  };

  const findByXY = async (lng: number, lat: number) => {
    setLoading(true);
    try {
      const p = await egibService.getParcelByXY(lng, lat);
      setParcel(p);
      if (p?.ParcelId) setIdInput(p.ParcelId);
    } catch {
      handleError("W tym miejscu nie znaleziono działki w EGiB.");
    } finally {
      setLoading(false);
    }
  };

  const handleLink = () => {
    if (!selectedCase) {
      handleError("Wybierz sprawę, do której chcesz powiązać działkę.");
      return;
    }
    if (!parcel?.ParcelId) return;
    linkMutation.mutate(
      {
        CaseId: selectedCase.Id,
        ParcelId: parcel.ParcelId,
        GeometryGeoJson: parcel.GeometryGeoJson,
        Wojewodztwo: parcel.Wojewodztwo,
        Powiat: parcel.Powiat,
        Gmina: parcel.Gmina,
        Obreb: parcel.Obreb,
      },
      {
        onSuccess: () => {
          refetchCaseParcels();
          notification({
            type: ENotificationType.SUCCESS,
            title: "EGiB",
            text: "Powiązano działkę ze sprawą.",
          });
        },
        onError: () => handleError("Nie udało się powiązać działki ze sprawą."),
      },
    );
  };

  const handleUnlink = (id: string) =>
    unlinkMutation.mutate(id, { onSuccess: () => refetchCaseParcels() });

  const showParcel = (gj?: string) => {
    if (!gj) return;
    try {
      setParcel((prev) => ({ ...prev, GeometryGeoJson: gj } as IEgibParcel));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.sidebar}>
        <div className={styles.title}>EGiB — Ewidencja Gruntów i Budynków</div>

        {/* Wyszukiwanie działki */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Wyszukaj działkę</div>
          <div className={styles.searchRow}>
            <TextField
              size="small"
              fullWidth
              label="Identyfikator działki"
              placeholder="np. 141201_1.0001.6509"
              value={idInput}
              onChange={(e) => setIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && findById()}
            />
            <Tooltip title="Szukaj">
              <span>
                <IconButton onClick={findById} disabled={loading || !idInput.trim()}>
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <div className={styles.hint}>
            …albo kliknij na mapie, aby zlokalizować działkę.
          </div>
        </div>

        {/* Identyfikatory wybranej działki */}
        {parcel && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Działka</div>
            <div className={styles.kv}>
              <span>Identyfikator</span>
              <b>{parcel.ParcelId || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Województwo</span>
              <b>{parcel.Wojewodztwo || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Powiat</span>
              <b>{parcel.Powiat || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Gmina</span>
              <b>{parcel.Gmina || "—"}</b>
            </div>
            <div className={styles.kv}>
              <span>Obręb</span>
              <b>{parcel.Obreb || "—"}</b>
            </div>
          </div>
        )}

        {/* Powiązanie ze sprawą */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Powiązanie ze sprawą</div>
          <Autocomplete
            size="small"
            options={cases}
            value={selectedCase}
            onChange={(_, v) => setSelectedCase(v)}
            getOptionLabel={(c) =>
              [c.Signature, c.Title].filter(Boolean).join(" — ") || c.Id
            }
            renderInput={(params) => (
              <TextField {...params} label="Sprawa / teczka" placeholder="Wybierz sprawę…" />
            )}
          />
          <Button
            className={styles.linkBtn}
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faLink} />}
            disabled={!parcel?.ParcelId || !selectedCase || linkMutation.isLoading}
            onClick={handleLink}
          >
            Powiąż działkę ze sprawą
          </Button>

          {selectedCase && (
            <div className={styles.linkedList}>
              <div className={styles.linkedTitle}>
                Działki sprawy ({caseParcels.length})
              </div>
              {caseParcels.map((cp) => (
                <div key={cp.Id} className={styles.linkedItem}>
                  <span
                    className={styles.linkedId}
                    title="Pokaż na mapie"
                    onClick={() => showParcel(cp.GeometryGeoJson)}
                  >
                    {cp.ParcelId}
                  </span>
                  <Tooltip title="Odepnij">
                    <IconButton size="small" onClick={() => handleUnlink(cp.Id)}>
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
              {caseParcels.length === 0 && (
                <div className={styles.hint}>Brak powiązanych działek.</div>
              )}
            </div>
          )}
        </div>

        {/* Warstwy */}
        <div className={styles.section}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showParcelsLayer}
                onChange={(e) => setShowParcelsLayer(e.target.checked)}
              />
            }
            label="Warstwa działek (KIEG)"
          />
        </div>
      </div>

      <div className={styles.mapWrap}>
        <MapContainer
          className={styles.map}
          center={[52.0, 19.4]}
          zoom={6}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {showParcelsLayer && (
            <WMSTileLayer
              url={KIEG_URL}
              layers="dzialki"
              format="image/png"
              transparent
              version="1.3.0"
              opacity={0.7}
            />
          )}
          {geom && (
            <>
              <GeoJSON
                key={parcel?.ParcelId ?? JSON.stringify(geom).slice(0, 32)}
                data={geom as any}
                style={{ color: "#5d49c7", weight: 2, fillOpacity: 0.15 }}
              />
              <FitToParcel geom={geom} />
            </>
          )}
          <ClickHandler onPick={findByXY} />
        </MapContainer>
      </div>
    </div>
  );
};

export default EgibModule;
